import Navbar from "../../components/Navbar";
import useAxios from "../../utils/useAxios";
import { useState, useEffect, useRef } from "react";
import { useToast } from "@chakra-ui/react";

import {
  Container,
  Box,
  VStack,
  StackDivider,
  useColorModeValue,
  Heading,
  Skeleton,
  Link,
  Text,
  SimpleGrid,
  Button,
  Select,
  Flex,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import DeviceTile from "../../components/DeviceComponents/DeviceTile";
import { IDevice } from "../../types/deviceInterface";
import { IField } from "../../types/fieldInterface";

type IDevices = IDevice[];

const deviceTypeLookup = new Map<number, string>([
  [-1, "Dragino Sensors"],
  [0, "Soil Sensors"],
  [1, "Soil Sensors"],
  [2, "AutoPlants"],
  [3, "Environment Sensors"],
  [4, "Relays"],
  [5, "Valves"],
  [6, "Valves With Flow Sensor"],
  [7, "Pumps"],
  [8, "Weather Stations"],
]);

const DeviceOverview = () => {
  const api = useAxios();
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [devices, setDevices] = useState<IDevices>([]);
  const box_color = useColorModeValue("white", "gray.900");
  const link_color = useColorModeValue("teal.600", "teal.200");
  const [fields, setFields] = useState<IField[]>([]);
  const flag = useRef(false);

  const [selectedField, setSelectedField] = useState<number>(0);
  const [selectedDeviceType, setSelectedDeviceType] = useState<number>(-2);

  const textColor = useColorModeValue("gray.800", "gray.200");

  useEffect(() => {
    if (flag.current) return;
    flag.current = true;

    const fetchAll = async () => {
      try {
        const [{ data: devicesData }, { data: fieldsData }] = await Promise.all([
          api.get("frontend/devices/current-data/"),
          api.get("/agtech/fields/"),
        ]);

        setDevices(devicesData);
        setFields(fieldsData);
      } catch (error: any) {
        if (error.response?.status === 401) return;
        toast({
          title: "Error fetching data",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAll();
  }, []);

  // Use devices directly as they include field info
  const mergedDevices: IDevice[] = devices;

  //Filter mergedDevices based on the selected field
  const filteredDevices = mergedDevices.filter((dev) => {
    // Filter by device type
    if (selectedDeviceType !== -2 && dev.device_type !== selectedDeviceType) {
      return false;
    }
    if (selectedField === -1) {
      return false;
    }
    if (selectedField === 0) {
      return true;
    }
    if (selectedField === 1) {
      return dev.field_id === null;
    }
    return dev.field_id === selectedField;
  });

  // Group devices by type
  const groupedDevices = filteredDevices.reduce((groups: Record<string, IDevice[]>, device) => {
    const deviceTypeName = deviceTypeLookup.get(device.device_type) || "UNKNOWN";
    if (!groups[deviceTypeName]) {
      groups[deviceTypeName] = [];
    }
    groups[deviceTypeName].push(device);
    return groups;
  }, {});

  return (
    <Navbar>
      <Box mx={{ base: 2, sm: 4, md: 6 }} p={5}>
        <VStack mt={2} spacing={4} align="stretch">
          <SimpleGrid minChildWidth={{ base: "40" }} spacing="5">
            <Heading fontSize={{ base: "2xl", sm: "3xl" }}>Device overview</Heading>
            <Link href="/add-device">
              <Button
                borderRadius={"unset"}
                p={2}
                flexShrink={0}
                fontStyle="semibold"
                _hover={{ bg: "blackAlpha.100" }}
                size="md"
                colorScheme="black"
                variant="outline"
                rightIcon={<ChevronRightIcon />}
              >
                Add New Device
              </Button>
            </Link>
          </SimpleGrid>

          <Flex direction={{ base: "column", md: "row" }} gap={6} mb={4}>
            <Box>
              <Text mb={2} fontWeight="semibold">
                Filter by field:
              </Text>
              <Select
                value={selectedField}
                onChange={(e) => setSelectedField(Number(e.target.value))}
                maxW="300px"
              >
                <option value={-1}>Select field...</option>
                <option value={0}>All fields</option>
                <option value={1}>Unattached (no field)</option>
                {fields.map((field) => (
                  <option key={field.id} value={field.id}>
                    {field.field_name}
                  </option>
                ))}
              </Select>
            </Box>
            <Box>
              <Text mb={2} fontWeight="semibold">
                Filter by device type:
              </Text>
              <Select
                value={selectedDeviceType}
                onChange={(e) => setSelectedDeviceType(Number(e.target.value))}
                maxW="300px"
              >
                <option value={-2}>All types</option>
                {Array.from(deviceTypeLookup)
                  .filter(([typeId]) => typeId !== 0)
                  .map(([typeId, typeName]) => (
                    <option key={typeId} value={typeId}>
                      {typeName}
                    </option>
                  ))}
              </Select>
            </Box>
          </Flex>

          {isLoading ? (
            <Skeleton height="20px" />
          ) : (
            Object.entries(groupedDevices).map(([deviceTypeName, devices]) => (
              <Box key={deviceTypeName} mt={4}>
                <Heading size="md" mb={2}>
                  {deviceTypeName}
                </Heading>
                <SimpleGrid minChildWidth="250px" spacing={4}>
                  {devices.map((device) => (
                    <Box
                      borderRadius="lg"
                      p={4}
                      boxShadow="lg"
                      bg={box_color}
                      key={device.device_id}
                      color={textColor}
                      width={{ base: "100%", md: "250px" }}
                    >
                      <Link
                        href={
                          device.device_type === 8
                            ? `/weather-station/${device.device_id}`
                            : device.device_type === 6
                            ? `/valve-view/${device.device_id}`
                            : `/devices/${device.device_id}`
                        }
                      >
                        <DeviceTile device={device} />
                      </Link>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            ))
          )}
        </VStack>
      </Box>
    </Navbar>
  );
};

export default DeviceOverview;
